var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import Tooltip from 'devextreme/ui/tooltip';
import { v4 as uuidv4 } from 'uuid';
import { msg, updateWhenLocaleChanges } from '@lit/localize';
/**
 * Represents the double indicator card
 */
let DinapsisDoubleIndicator = class DinapsisDoubleIndicator extends LitElement {
    constructor() {
        super();
        /**
         * A property that is used to set the card height
         */
        this.height = 'auto';
        /**
         * A property that is used to set the card width
         */
        this.width = '250px';
        /**
         * Property used to set the text that will appear if there isn't data for the 'dataUnit' property.
         * Default value is 'sin datos'
         */
        this.textNoData = '';
        /**
         * Property used to set the color of texts and icons when there isn't data.
         * Default value is #e64949.
         */
        this.colorNoData = '#e64949';
        /**
         * A property that is used to set the complementary text
         */
        this.complementaryText = '';
        /**
         * A property that is used to set multi indicator data
         */
        this.indicatorData = [];
        /**
         * A property that is used to set the icon of the card
         */
        this.icon = '';
        /**
         * A property that is used to set if the card is clickable or not
         */
        this.clickable = false;
        /**
         * A property that is used to configure the column options. Mainly for styles.
         */
        this.columnOptions = { widthLeft: '50%', widthRight: '50%' };
        /**
         * A property that is used to set the component to show in mode card or not
         */
        this.isCard = true;
        /**
         * A private variable for set unique id for tooltips
         */
        this.tooltipRandomId = uuidv4();
        updateWhenLocaleChanges(this);
    }
    /**
     * The createRenderRoot function is a function that is called by the lit-html library to tell the
     * component where to render the html.
     * @returns The element itself.
     */
    createRenderRoot() {
        return this;
    }
    /**
     * A function that emit an event when the card icon is clicked
     */
    handleClick(e) {
        e.stopPropagation();
        this.dispatchEvent(new CustomEvent('click-icon'));
    }
    /**
     * A function that emit an event when the card is clicked
     */
    handleCardClick(e) {
        e.stopPropagation();
        if (!this.clickable)
            return;
        this.dispatchEvent(new Event('click'));
    }
    /**
     * A function that render a div with an icon.
     * If it is not a card, nothing is returned
     * @returns Returns a html template.
     */
    indicatorCardIcon() {
        if (!this.isCard)
            return html ``;
        if (this.icon === '')
            return '';
        return html `
      <div class="indicator-card-icon">
        <span class="material-icons-round double-indicator-icon" @click=${this.handleClick}>
          ${this.icon}
        </span>
      </div>
    `;
    }
    /**
     * A function that print the indicator container.
     * @param indicatorPosition This param represent the position of the indicator.
     * @param indicatorData This param represent the data to print of the indicator.
     * @returns Returns a html template.
     */
    indicatorContainer(indicatorPosition, indicatorData) {
        const textNoData = this.textNoData === '' ? msg('text_no_data') : this.textNoData;
        return html `
      <div class="indicator-container-multi">
        <span 
          class="indicator-title"
          id="${indicatorPosition}-indicator-title-${this.tooltipRandomId}"
          style="width: ${this.columnOptions.widthLeft};"
        >${indicatorData.title}</span>
        <div class="indicator-data-container"
          style="width: ${this.columnOptions.widthRight};"
        >
          <span
            class="indicator-data-unit"
            id="${indicatorPosition}-indicator-data-unit-${this.tooltipRandomId}"
            style="color: ${indicatorData.dataUnit === '' ? this.colorNoData : ''};"
          >
            ${indicatorData.dataUnit === '' ? textNoData : indicatorData.dataUnit}
          </span>
          <span
            class="indicator-data-detail"
            id="${indicatorPosition}-indicator-data-detail-${this.tooltipRandomId}"
          >
            ${indicatorData.dataDetail}
          </span>
        </div>
      </div>
    `;
    }
    /**
     * A function that recive one element and check if the text overflow or not.
     * @param el This param represents the element that will be checked if it overflows.
     * @returns Returns true if the element overflow and false if it doesn't.
     */
    checkOverflow(el) {
        const curOverflow = el.style.overflow;
        const element = el;
        const isOverflowing = el.scrollWidth > el.offsetWidth
            || (el.clientHeight * 2) < el.scrollHeight;
        element.style.overflow = curOverflow;
        return isOverflowing;
    }
    /**
     * A function that recives two params and generate a tooltip.
     * @param tooltipId This param represents the id of the tooltip.
     * @param target This param represents the container the tooltip points to.
     */
    generateTooltip(tooltipId, target) {
        // Select and remove the previous tooltip
        const tooltipContainer = document.getElementById(tooltipId);
        if (tooltipContainer)
            tooltipContainer.remove();
        // Select the container where the tooltip will be painted
        const targetContainer = document.getElementById(target);
        if (!targetContainer)
            return;
        const txtTarget = targetContainer.textContent;
        // Create the tooltip div and set id and target text
        const newTooltipContainer = document.createElement('div');
        newTooltipContainer.id = tooltipId;
        newTooltipContainer.innerHTML = txtTarget !== null && txtTarget !== void 0 ? txtTarget : '';
        // Add to target the tooltip container
        targetContainer.append(newTooltipContainer);
        // Check if target needed tooltip and set or remove class
        if (targetContainer && !this.checkOverflow(targetContainer)) {
            newTooltipContainer.classList.add('hide-tooltip');
        }
        else {
            newTooltipContainer.classList.remove('hide-tooltip');
        }
        // Check if exist target, tooltip and tooltip doesn't have hide-tooltip class
        if (targetContainer
            && newTooltipContainer
            && !(newTooltipContainer === null || newTooltipContainer === void 0 ? void 0 : newTooltipContainer.classList.contains('hide-tooltip'))) {
            const tooltip = new Tooltip(newTooltipContainer);
            tooltip.option('target', targetContainer);
            tooltip.option('showEvent', 'mouseenter');
            tooltip.option('hideEvent', 'mouseleave');
        }
    }
    /**
     * A function that setup the tooltips of the component
     */
    setupTooltips() {
        const container = document.getElementById('double-indicator-container');
        const tooltips = [];
        tooltips.push({ tooltipId: `tooltip-complementary-text-${this.tooltipRandomId}`, target: `complementary-text-${this.tooltipRandomId}` });
        this.indicatorData.forEach((_i, key) => {
            tooltips.push({ tooltipId: `tooltip-${key}-indicator-title-${this.tooltipRandomId}`, target: `${key}-indicator-title-${this.tooltipRandomId}` });
            tooltips.push({ tooltipId: `tooltip-${key}-indicator-data-unit-${this.tooltipRandomId}`, target: `${key}-indicator-data-unit-${this.tooltipRandomId}` });
            tooltips.push({ tooltipId: `tooltip-${key}-indicator-data-detail-${this.tooltipRandomId}`, target: `${key}-indicator-data-detail-${this.tooltipRandomId}` });
        });
        if (container) {
            tooltips.forEach((tooltip) => {
                this.generateTooltip(tooltip.tooltipId, tooltip.target);
            });
        }
    }
    /**
     * The above function is a lifecycle hook that is called when the component is updated.
     * It is used to initialize the tree view.
     */
    updated() {
        this.setupTooltips();
    }
    /**
     * The function returns a string of HTML that contains a div that contains the component.
     * @returns The return statement is returning the html template literal
     */
    render() {
        return html `
      <div
        class="
          double-indicator-container
          ${this.isCard ? 'dinapsis-card dinapsis-card-shadowed' : ''}
          ${this.clickable ? 'clickable-card' : ''}
        "
        id="double-indicator-container"
        style="height: ${this.height}; width: ${this.width}"
        @click=${this.handleCardClick}
      >
        ${this.indicatorCardIcon()}
        <div class="indicator-child">
          <div class="complementary-text-container">
            <span class="complementary-text" id="complementary-text-${this.tooltipRandomId}">
              ${this.complementaryText}
            </span>
          </div>
        </div>
          ${this.indicatorData.map((item, index) => this.indicatorContainer(index, item))}
      </div>
    `;
    }
};
__decorate([
    property({ type: String })
], DinapsisDoubleIndicator.prototype, "height", void 0);
__decorate([
    property({ type: String })
], DinapsisDoubleIndicator.prototype, "width", void 0);
__decorate([
    property({ type: String })
], DinapsisDoubleIndicator.prototype, "textNoData", void 0);
__decorate([
    property({ type: String })
], DinapsisDoubleIndicator.prototype, "colorNoData", void 0);
__decorate([
    property({ type: String })
], DinapsisDoubleIndicator.prototype, "complementaryText", void 0);
__decorate([
    property({ type: Array })
], DinapsisDoubleIndicator.prototype, "indicatorData", void 0);
__decorate([
    property({ type: String })
], DinapsisDoubleIndicator.prototype, "icon", void 0);
__decorate([
    property({ type: Boolean })
], DinapsisDoubleIndicator.prototype, "clickable", void 0);
__decorate([
    property({ type: Object })
], DinapsisDoubleIndicator.prototype, "columnOptions", void 0);
__decorate([
    property({ type: Boolean })
], DinapsisDoubleIndicator.prototype, "isCard", void 0);
DinapsisDoubleIndicator = __decorate([
    customElement('dinapsis-double-indicator')
], DinapsisDoubleIndicator);
export { DinapsisDoubleIndicator };
//# sourceMappingURL=dinapsis-double-indicator.js.map